import { Dropdown, DropdownProps } from 'components/form/inputs/Dropdown'
import {
  departmentStyles,
  observationTypeStyles,
  severityStyles,
} from 'pages/pointClouds/components/dropdowns/EnumStyles'
import React, { useMemo } from 'react'
import { Department, DropdownOption, ObservationType, Severity } from 'types'

interface EnumStyle {
  color?: string
  icon?: React.ReactNode
  label?: string
}
type EnumStyleMap<T extends string> = Record<T, EnumStyle>

const createDropdownOptions = <T extends string>(
  enumValues: T[],
  styles: EnumStyleMap<T>,
  prefix: string = ''
): DropdownOption[] => {
  return enumValues.map((val) => {
    const style = styles[val] || {}
    const displayLabel = style.label || val
    return {
      value: val,
      label: (
        <span
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            color: style.color,
            fontSize: '12px',
          }}>
          {style.icon}
          {prefix}
          {displayLabel}
        </span>
      ),
    }
  })
}

// Update BaseDropdownProps to include common styling/config props
type BaseDropdownProps = Omit<DropdownProps, 'options'> & {
  placeholder?: string // Made placeholder optional
}

// Define common props with defaults
const commonDropdownProps = {
  $backgroundColor: '#2a2a2a',
  $borderColor: '#444',
  searchable: false,
}

export const SeverityDropdown: React.FC<BaseDropdownProps> = ({
  placeholder = 'Severity',
  ...props
}) => {
  const options = useMemo(
    () => createDropdownOptions(Object.values(Severity), severityStyles),
    []
  )
  return (
    <Dropdown
      options={options}
      placeholder={placeholder}
      $itemKey='severity-dropdown' // Keep itemKey specific
      {...commonDropdownProps}
      {...props} // Allow overrides
    />
  )
}

export const ObservationTypeDropdown: React.FC<BaseDropdownProps> = ({
  placeholder = 'Type',
  ...props
}) => {
  const options = useMemo(
    () =>
      createDropdownOptions(
        Object.values(ObservationType),
        observationTypeStyles
      ),
    []
  )
  return (
    <Dropdown
      options={options}
      placeholder={placeholder}
      $itemKey='observation-type-dropdown' // Keep itemKey specific
      {...commonDropdownProps}
      {...props} // Allow overrides
    />
  )
}

export const DepartmentDropdown: React.FC<BaseDropdownProps> = ({
  placeholder = 'Department',
  ...props
}) => {
  const options = useMemo(
    () => createDropdownOptions(Object.values(Department), departmentStyles),
    []
  )
  return (
    <Dropdown
      options={options}
      placeholder={placeholder}
      $itemKey='department-dropdown' // Keep itemKey specific
      {...commonDropdownProps}
      {...props} // Allow overrides
    />
  )
}

export { createDropdownOptions }
